"use client";

import { useParams, useSearchParams } from "next/navigation";
import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { MapPin, ExternalLink, ImageIcon, Info, UtensilsCrossed, MessageSquare, Navigation } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { appendQueryParams } from "@/lib/app-store-urls";
import { useLanguage } from "@/lib/useLanguage";
import { ClaimBanner } from "@/components/ClaimBanner";
import { CommunityCaptureCta } from "@/components/product/CommunityCaptureCta";
import { MenuBadgeRow } from "@/components/menus/MenuBadgeRow";
import { getMenuListThumbnail } from "@/lib/menus/getMenuThumbnail";
import {
  resolveCanonicalRestaurantId,
  fetchRestaurantPublicStatus,
  isPubliclyClaimed,
} from "@menuhunter/core/publicRestaurantStatus";
import { instagramHandleHasRestaurantAffinity } from "@menuhunter/core/instagramHandleRestaurantMatch";
import { formatPriceEurIfPresent } from "@/lib/formatPriceEur";
import { logRestaurantEngagement } from "@/lib/logRestaurantEngagement";
import {
  getQrAttributionKind,
  isQrAttributionSource,
} from "@/lib/restaurantPublicQrUrl";
import { resolveRestaurantCityLabel } from "@/lib/seo/restaurant-city-label";
import { ChristmasMenuSection } from "@/components/restaurant/ChristmasMenuSection";
import {
  parseShareChristmasOfferings,
  parseWebMenuCategoryParam,
  resolveInitialWebMenuCategory,
  resolveWebMenuCategoryMode,
  type ShareChristmasOfferingSummary,
  type WebMenuCategory,
} from "@/lib/christmas/shareBundleChristmas";
import {
  captureChristmasWebCategoryChanged,
  captureChristmasWebQrRestaurantInteraction,
} from "@/lib/christmas/webChristmasAnalytics";

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

type TabId = "menu" | "fotos" | "resenas" | "info" | "llegar" | "reclamar";

type ShareMenu = {
  id: string;
  restaurant_id?: string;
  price_eur: number | null;
  photo_url: string | null;
  thumbnail_url: string | null;
  chalkboard_image_url?: string | null;
  hero_image_url?: string | null;
  captured_at: string | null;
  publish_channel?: string | null;
};

type ShareRestaurant = {
  id: string;
  name: string | null;
  address_text?: string | null;
  city_slug?: string | null;
  /** Server-resolved display city; never "Other". */
  city_label?: string | null;
  phone?: string | null;
  lat?: number | null;
  lng?: number | null;
  instagram_handle?: string | null;
  website_url?: string | null;
  google_places_url?: string | null;
  has_terraza?: boolean | null;
  opening_hours?: unknown | null;
};

type ShareBundle = {
  restaurant: ShareRestaurant;
  menus: ShareMenu[];
  christmas_offerings?: ShareChristmasOfferingSummary[];
};

export type PublicRestaurantInitialData = ShareBundle;

function formatLastMenuDate(capturedAt: string | null | undefined): string | null {
  if (!capturedAt) return null;
  const d = new Date(capturedAt);
  if (!Number.isFinite(d.getTime())) return null;
  return d.toLocaleDateString("es-ES", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function collectPhotoUrls(menus: ShareMenu[]): string[] {
  const seen = new Set<string>();
  const urls: string[] = [];
  for (const m of menus) {
    for (const u of [m.photo_url, m.thumbnail_url, m.chalkboard_image_url, m.hero_image_url]) {
      if (u && !seen.has(u)) {
        seen.add(u);
        urls.push(u);
      }
    }
  }
  return urls;
}

export function PublicRestaurantPageContent({
  initialData,
}: {
  initialData?: PublicRestaurantInitialData | null;
} = {}) {
  const params = useParams();
  const searchParams = useSearchParams();
  const id = typeof params?.id === "string" ? params.id : null;
  const { t } = useLanguage();
  const rs = t.restaurantSharePage;
  const TABS: { id: TabId; label: string; icon: typeof UtensilsCrossed }[] = [
    { id: "menu", label: rs?.tabMenu ?? "Menú", icon: UtensilsCrossed },
    { id: "fotos", label: rs?.tabPhotos ?? "Fotos", icon: ImageIcon },
    { id: "resenas", label: rs?.tabReviews ?? "Reseñas", icon: MessageSquare },
    { id: "info", label: rs?.tabInfo ?? "Info", icon: Info },
    { id: "llegar", label: rs?.tabDirections ?? "Cómo llegar", icon: Navigation },
    { id: "reclamar", label: rs?.tabClaim ?? "Gestionar", icon: MapPin },
  ];
  const hasInitial = !!(initialData?.restaurant?.id);
  const initialChristmas = parseShareChristmasOfferings(
    initialData?.christmas_offerings
  );
  const [bundle, setBundle] = useState<ShareBundle | null>(
    initialData
      ? { ...initialData, christmas_offerings: initialChristmas }
      : null
  );
  const [loading, setLoading] = useState(!hasInitial);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>("menu");
  const [isClaimed, setIsClaimed] = useState<boolean | undefined>(undefined);
  const requestedCategory = parseWebMenuCategoryParam(
    searchParams?.get("category")
  );
  const deepLinkOfferingIdRaw = searchParams?.get("offering");
  const deepLinkOfferingId =
    deepLinkOfferingIdRaw && UUID_REGEX.test(deepLinkOfferingIdRaw)
      ? deepLinkOfferingIdRaw
      : null;
  const christmasOfferings = bundle?.christmas_offerings ?? [];
  const menuMode = resolveWebMenuCategoryMode(
    bundle?.menus?.length ?? 0,
    christmasOfferings.length
  );
  const [menuCategory, setMenuCategory] = useState<WebMenuCategory>(() =>
    resolveInitialWebMenuCategory(menuMode, requestedCategory, deepLinkOfferingId)
  );

  const downloadUrl = "https://menuhunter.app/download";

  const openInAppHref = useMemo(() => {
    if (!id || !UUID_REGEX.test(id)) return "#";
    return appendQueryParams(`menuhunter:///restaurant/${id}`, searchParams);
  }, [id, searchParams]);

  useEffect(() => {
    setMenuCategory(
      resolveInitialWebMenuCategory(menuMode, requestedCategory, deepLinkOfferingId)
    );
  }, [menuMode, requestedCategory, deepLinkOfferingId]);

  useEffect(() => {
    if (!id || christmasOfferings.length < 1) return;
    const sourceParam = searchParams?.get("source") ?? searchParams?.get("utm_source");
    if (!isQrAttributionSource(sourceParam)) return;
    captureChristmasWebQrRestaurantInteraction({
      restaurant_id: id,
      christmas_count: christmasOfferings.length,
      daily_count: bundle?.menus?.length ?? 0,
    });
  }, [id, christmasOfferings.length, bundle?.menus?.length, searchParams]);

  useEffect(() => {
    if (!id || !UUID_REGEX.test(id)) {
      if (!hasInitial) {
        setError("invalid");
        setLoading(false);
      }
      return;
    }

    let cancelled = false;
    const sourceParam = searchParams?.get("source") ?? searchParams?.get("utm_source");
    const fromQr = isQrAttributionSource(sourceParam);
    const qrKind = getQrAttributionKind(sourceParam);

    (async () => {
      try {
        // When SSR already provided the restaurant, keep it visible — only refresh in background.
        if (!hasInitial) {
          setLoading(true);
          setError(null);
        }
        const { data, error: rpcError } = await supabase.rpc("get_public_restaurant_share_bundle", {
          p_restaurant_id: id,
        });

        if (cancelled) return;

        if (rpcError) {
          if (!hasInitial) {
            setError("fetch");
            setBundle(null);
          }
          return;
        }

        if (data == null || (typeof data === "object" && data !== null && !("restaurant" in data))) {
          if (!hasInitial) {
            setBundle(null);
            setError("notfound");
          }
          return;
        }

        const parsed = data as {
          restaurant: ShareRestaurant;
          menus: ShareMenu[];
          christmas_offerings?: unknown;
        };
        if (!parsed.restaurant?.id) {
          if (!hasInitial) {
            setBundle(null);
            setError("notfound");
          }
          return;
        }

        const city_label =
          parsed.restaurant.city_label ??
          resolveRestaurantCityLabel({
            city_slug: parsed.restaurant.city_slug,
            address_text: parsed.restaurant.address_text,
          });

        setBundle({
          restaurant: { ...parsed.restaurant, city_label },
          menus: Array.isArray(parsed.menus) ? parsed.menus : [],
          christmas_offerings: parseShareChristmasOfferings(
            parsed.christmas_offerings
          ),
        });

        void logRestaurantEngagement(supabase, {
          restaurantId: parsed.restaurant.id,
          eventType: "restaurant_view",
        });
        if (fromQr) {
          void logRestaurantEngagement(supabase, {
            restaurantId: parsed.restaurant.id,
            eventType: "qr_scan",
            metadata: { qr_source: qrKind === "sticker" ? "sticker" : "qr" },
          });
        }

        const canonical = await resolveCanonicalRestaurantId(supabase, parsed.restaurant.id);
        const { map, error: statusError } = await fetchRestaurantPublicStatus(supabase, [canonical]);
        if (!cancelled && !statusError) {
          setIsClaimed(isPubliclyClaimed(map.get(canonical)));
        }
      } catch {
        if (!cancelled && !hasInitial) setError("fetch");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id, searchParams, hasInitial]);

  if (!id || !UUID_REGEX.test(id)) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-white dark:bg-gray-900">
        <h1 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          {t.restaurantLanding.invalidId}
        </h1>
        <Link href="/" className="text-blue-600 hover:underline">
          {t.restaurantSharePage.backHome}
        </Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-300">{t.restaurantSharePage.loading}</p>
        </div>
      </div>
    );
  }

  if (error === "fetch") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-white dark:bg-gray-900">
        <h1 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          {t.restaurantSharePage.errorTitle}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 text-center max-w-md mb-6">
          {t.restaurantSharePage.errorBody}
        </p>
        <Link href="/" className="text-blue-600 hover:underline">
          {t.restaurantSharePage.backHome}
        </Link>
      </div>
    );
  }

  if (error === "notfound" || !bundle) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-white dark:bg-gray-900">
        <h1 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          {t.restaurantSharePage.notFoundTitle}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 text-center max-w-md mb-6">
          {t.restaurantSharePage.notFoundBody}
        </p>
        <Link href="/" className="text-blue-600 hover:underline">
          {t.restaurantSharePage.backHome}
        </Link>
      </div>
    );
  }

  const { restaurant, menus } = bundle;
  const cityLabel =
    restaurant.city_label ??
    resolveRestaurantCityLabel({
      city_slug: restaurant.city_slug,
      address_text: restaurant.address_text,
    });
  const photos = collectPhotoUrls(menus);
  const latestMenu = menus[0] ?? null;
  const previousMenus = menus.slice(1);
  const mapsQuery = encodeURIComponent(
    restaurant.lat != null && restaurant.lng != null
      ? `${restaurant.lat},${restaurant.lng}`
      : [restaurant.name, restaurant.address_text, cityLabel].filter(Boolean).join(", ")
  );
  const directionsHref =
    restaurant.google_places_url ??
    (mapsQuery ? `https://www.google.com/maps/search/?api=1&query=${mapsQuery}` : null);
  const mapEmbedQuery =
    restaurant.lat != null && restaurant.lng != null
      ? `${restaurant.lat},${restaurant.lng}`
      : [restaurant.address_text, restaurant.name, cityLabel].filter(Boolean).join(", ");
  const latestMenuDateLabel = formatLastMenuDate(latestMenu?.captured_at);
  const uploadHref = `/download?restaurant_id=${encodeURIComponent(id ?? restaurant.id)}`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-gray-900 dark:to-gray-800 pb-10">
      <div className="sticky top-0 z-20 border-b border-blue-100 dark:border-gray-700 bg-white/95 dark:bg-gray-900/95 backdrop-blur">
        <div className="max-w-2xl mx-auto px-4 py-3 flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-gray-700 dark:text-gray-200 flex-1 min-w-[200px]">
            {t.restaurantSharePage.ctaHint}
          </p>
          <div className="flex flex-wrap gap-2">
            <a
              href={openInAppHref}
              className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
            >
              {t.restaurantSharePage.openInApp}
            </a>
            <a
              href={downloadUrl}
              className="inline-flex items-center justify-center rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-2 text-sm font-semibold text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              {t.restaurantSharePage.downloadApp}
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="bg-white dark:bg-gray-900 rounded-lg shadow-lg p-5 mb-4">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-1">
            {restaurant.name}
          </h1>
          {(restaurant.address_text || cityLabel) && (
            <p className="text-gray-600 dark:text-gray-300 text-sm flex items-start gap-1.5">
              <MapPin className="w-4 h-4 shrink-0 mt-0.5" />
              <span>
                {restaurant.address_text}
                {restaurant.address_text && cityLabel ? " · " : ""}
                {cityLabel}
              </span>
            </p>
          )}
          {latestMenuDateLabel && (
            <p className="mt-2 text-sm font-semibold text-gray-800 dark:text-gray-100">
              {(rs?.lastMenuLabel ?? "Último menú") + ": "}
              {latestMenuDateLabel}
            </p>
          )}
          {restaurant.phone && (
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              <a href={`tel:${restaurant.phone}`} className="hover:underline">
                {restaurant.phone}
              </a>
            </p>
          )}
          {mapEmbedQuery && (
            <div className="mt-4 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 aspect-[16/9] bg-gray-100">
              <iframe
                title={`Mapa de ${restaurant.name ?? "restaurante"}`}
                src={`https://maps.google.com/maps?q=${encodeURIComponent(mapEmbedQuery)}&output=embed`}
                className="w-full h-full min-h-[180px] border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          )}
          <a
            href={uploadHref}
            className="mt-4 inline-flex items-center justify-center rounded-lg bg-orange-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-orange-600"
          >
            {rs?.uploadMenuCta ?? "¿Has comido aquí hoy? Sube el menú"}
          </a>
        </div>

        <nav
          className="flex gap-1 overflow-x-auto scrollbar-hide mb-4 -mx-1 px-1"
          aria-label="Secciones del restaurante"
        >
          {TABS.map(({ id: tabId, label, icon: Icon }) => (
            <button
              key={tabId}
              type="button"
              onClick={() => setActiveTab(tabId)}
              className={`shrink-0 inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs sm:text-sm font-medium transition-colors ${
                activeTab === tabId
                  ? "bg-blue-600 text-white"
                  : "bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700"
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
            </button>
          ))}
        </nav>

        <div className="bg-white dark:bg-gray-900 rounded-lg shadow-md p-4 sm:p-5 min-h-[200px]">
          {activeTab === "menu" && (
            <MenuTab
              menus={menus}
              latestMenu={latestMenu}
              previousMenus={previousMenus}
              restaurantName={restaurant.name}
              restaurantId={id ?? restaurant.id}
              isClaimed={isClaimed}
              searchParams={searchParams}
              t={t}
              menuMode={menuMode}
              menuCategory={menuCategory}
              onMenuCategoryChange={setMenuCategory}
              christmasOfferings={christmasOfferings}
              highlightOfferingId={deepLinkOfferingId}
            />
          )}
          {activeTab === "fotos" && <PhotosTab photos={photos} restaurantName={restaurant.name} />}
          {activeTab === "resenas" && <ReviewsTab />}
          {activeTab === "info" && (
            <InfoTab
              restaurant={restaurant}
              cityLabel={cityLabel}
              isClaimed={isClaimed}
              restaurantId={id ?? restaurant.id}
            />
          )}
          {activeTab === "llegar" && (
            <DirectionsTab
              address={restaurant.address_text}
              directionsHref={directionsHref}
              restaurantName={restaurant.name}
              restaurantId={id}
            />
          )}
          {activeTab === "reclamar" && (
            <ClaimTab
              restaurantId={id}
              restaurantName={restaurant.name}
              isClaimed={isClaimed}
            />
          )}
        </div>

        <div className="mt-6">
          <CommunityCaptureCta />
        </div>

        <div className="text-center mt-8">
          <Link href="/" className="text-blue-600 hover:text-blue-800 font-semibold">
            {t.restaurantSharePage.backHome}
          </Link>
        </div>
      </div>
    </div>
  );
}

function MenuTab({
  menus,
  latestMenu,
  previousMenus,
  restaurantName,
  restaurantId,
  isClaimed,
  searchParams,
  t,
  menuMode,
  menuCategory,
  onMenuCategoryChange,
  christmasOfferings,
  highlightOfferingId,
}: {
  menus: ShareMenu[];
  latestMenu: ShareMenu | null;
  previousMenus: ShareMenu[];
  restaurantName: string | null;
  restaurantId: string;
  isClaimed?: boolean;
  searchParams: ReturnType<typeof useSearchParams>;
  t: ReturnType<typeof useLanguage>["t"];
  menuMode: ReturnType<typeof resolveWebMenuCategoryMode>;
  menuCategory: WebMenuCategory;
  onMenuCategoryChange: (next: WebMenuCategory) => void;
  christmasOfferings: ShareChristmasOfferingSummary[];
  highlightOfferingId?: string | null;
}) {
  const rs = t.restaurantSharePage;
  const showChristmas =
    menuCategory === "christmas" && menuMode !== "daily-only";
  const categoryChristmasLabel = (rs.categoryChristmas ?? "Navidad ({count})").replace(
    "{count}",
    String(christmasOfferings.length)
  );

  return (
    <div>
      {menuMode === "both" && (
        <div
          className="flex flex-wrap gap-2 mb-4"
          role="tablist"
          aria-label="Tipo de menú"
        >
          <button
            type="button"
            role="tab"
            aria-selected={menuCategory === "daily"}
            onClick={() => {
              captureChristmasWebCategoryChanged({
                menu_category: "daily",
                restaurant_id: restaurantId,
                from_qr: isQrAttributionSource(
                  searchParams?.get("source") ?? searchParams?.get("utm_source")
                ),
              });
              onMenuCategoryChange("daily");
            }}
            className={`shrink-0 rounded-lg px-3 py-1.5 text-xs sm:text-sm font-semibold border transition-colors min-h-10 ${
              menuCategory === "daily"
                ? "bg-blue-600 text-white border-blue-600"
                : "bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700"
            }`}
          >
            {rs.categoryDaily ?? "Menú del Día"}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={menuCategory === "christmas"}
            onClick={() => {
              captureChristmasWebCategoryChanged({
                menu_category: "christmas",
                restaurant_id: restaurantId,
                from_qr: isQrAttributionSource(
                  searchParams?.get("source") ?? searchParams?.get("utm_source")
                ),
              });
              onMenuCategoryChange("christmas");
            }}
            className={`shrink-0 rounded-lg px-3 py-1.5 text-xs sm:text-sm font-semibold border transition-colors min-h-10 ${
              menuCategory === "christmas"
                ? "bg-blue-600 text-white border-blue-600"
                : "bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700"
            }`}
          >
            {categoryChristmasLabel}
          </button>
        </div>
      )}

      {showChristmas ? (
        <ChristmasMenuSection
          offerings={christmasOfferings}
          restaurantName={restaurantName}
          highlightOfferingId={highlightOfferingId}
          rs={rs}
        />
      ) : menus.length === 0 ? (
        <div className="text-center py-6 text-gray-600 dark:text-gray-400">
          <p>{rs.noMenus}</p>
          <CommunityCaptureCta variant="compact" className="mt-4 justify-center" />
        </div>
      ) : (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
            {rs.menusHeading}
          </h2>
          {latestMenu && (
            <>
              {latestMenu.captured_at && (
                <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-3">
                  {(rs.lastMenuLabel ?? "Último menú") + ": "}
                  {formatLastMenuDate(latestMenu.captured_at)}
                </p>
              )}
              <MenuCard
                menu={latestMenu}
                restaurantName={restaurantName}
                isClaimed={isClaimed}
                searchParams={searchParams}
                highlight
              />
              <a
                href={`/download?restaurant_id=${encodeURIComponent(restaurantId)}`}
                className="mt-4 inline-flex text-sm font-semibold text-orange-600 dark:text-orange-400 hover:underline"
              >
                {rs.uploadMenuCta ?? "¿Has comido aquí hoy? Sube el menú"}
              </a>
            </>
          )}
          {previousMenus.length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                {rs.pastMenusHeading ?? "Menús anteriores"}
              </h3>
              <ul className="space-y-3">
                {previousMenus.map((m) => (
                  <li key={m.id}>
                    <MenuCard
                      menu={m}
                      restaurantName={restaurantName}
                      isClaimed={isClaimed}
                      searchParams={searchParams}
                    />
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function MenuCard({
  menu,
  restaurantName,
  isClaimed,
  searchParams,
  highlight,
}: {
  menu: ShareMenu;
  restaurantName: string | null;
  isClaimed?: boolean;
  searchParams: ReturnType<typeof useSearchParams>;
  highlight?: boolean;
}) {
  const thumb = getMenuListThumbnail(menu) ?? menu.hero_image_url;
  const priceText = formatPriceEurIfPresent(menu.price_eur);

  return (
    <Link
      href={appendQueryParams(`/menu/${menu.id}`, searchParams)}
      className={`flex gap-4 rounded-lg border p-3 hover:shadow-md transition-shadow ${
        highlight
          ? "border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-950/20"
          : "border-gray-100 dark:border-gray-800"
      }`}
    >
      {thumb ? (
        <div className="relative w-20 h-20 sm:w-24 sm:h-24 shrink-0 rounded-md overflow-hidden bg-gray-100">
          <Image src={thumb} alt="" width={96} height={96} className="object-cover w-full h-full" unoptimized />
        </div>
      ) : (
        <div className="w-20 h-20 sm:w-24 sm:h-24 shrink-0 rounded-md bg-gray-100 dark:bg-gray-800" />
      )}
      <div className="flex-1 min-w-0">
        {highlight && (
          <p className="text-[10px] uppercase tracking-wide font-semibold text-blue-600 dark:text-blue-400 mb-1">
            Menú más reciente
          </p>
        )}
        <MenuBadgeRow
          capturedAt={menu.captured_at}
          isRestaurantClaimed={isClaimed}
          className="mb-1.5"
        />
        {priceText && (
          <span className="inline-block bg-orange-500 text-white text-sm px-2.5 py-0.5 rounded-full font-semibold">
            {priceText}
          </span>
        )}
        <p className="text-sm text-blue-600 mt-2">{restaurantName} — Ver menú →</p>
      </div>
    </Link>
  );
}

function PhotosTab({ photos, restaurantName }: { photos: string[]; restaurantName: string | null }) {
  if (photos.length === 0) {
    return (
      <p className="text-sm text-gray-600 dark:text-gray-400 text-center py-8">
        Aún no hay fotos públicas de este restaurante.
      </p>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
      {photos.map((url) => (
        <div
          key={url}
          className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800"
        >
          <Image
            src={url}
            alt={restaurantName ? `Foto de ${restaurantName}` : "Foto del restaurante"}
            fill
            unoptimized
            className="object-cover"
            sizes="(min-width: 640px) 33vw, 50vw"
          />
        </div>
      ))}
    </div>
  );
}

function ReviewsTab() {
  const { t } = useLanguage();
  const rs = t.restaurantSharePage;
  return (
    <div className="text-center py-8 px-2">
      <MessageSquare className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
      <p className="text-sm text-gray-700 dark:text-gray-300 font-medium">{rs.reviewsTabTitle}</p>
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 max-w-sm mx-auto leading-relaxed">
        {rs.reviewsTabBody}
      </p>
      <Link
        href="/download"
        className="inline-flex mt-4 text-sm font-semibold text-blue-600 hover:underline"
      >
        {rs.downloadApp}
      </Link>
    </div>
  );
}

function formatOpeningHoursSummary(hours: unknown): string | null {
  if (!hours) return null;
  if (typeof hours === "string" && hours.trim()) return hours.trim();
  if (Array.isArray(hours)) {
    const lines = hours
      .map((h) => {
        if (typeof h === "string") return h;
        if (h && typeof h === "object") {
          const o = h as Record<string, unknown>;
          const days = typeof o.dayOfWeek === "string" ? o.dayOfWeek : null;
          const open = typeof o.opens === "string" ? o.opens : null;
          const close = typeof o.closes === "string" ? o.closes : null;
          if (days && open && close) return `${days}: ${open}–${close}`;
        }
        return null;
      })
      .filter(Boolean);
    return lines.length ? lines.join("; ") : null;
  }
  if (typeof hours === "object") {
    try {
      return JSON.stringify(hours);
    } catch {
      return null;
    }
  }
  return null;
}

function InfoTab({
  restaurant,
  cityLabel,
  isClaimed,
  restaurantId,
}: {
  restaurant: ShareRestaurant;
  cityLabel: string | null;
  isClaimed?: boolean;
  restaurantId: string;
}) {
  const { t } = useLanguage();
  const rs = t.restaurantSharePage;
  const igHandle = restaurant.instagram_handle?.replace(/^@/, "").trim() || null;
  const showInstagram =
    !!igHandle &&
    (!!isClaimed ||
      instagramHandleHasRestaurantAffinity(igHandle, restaurant.name ?? ""));
  const hoursSummary = formatOpeningHoursSummary(restaurant.opening_hours);
  return (
    <div className="space-y-4 text-sm">
      {restaurant.address_text && (
        <p className="flex gap-2 text-gray-700 dark:text-gray-300">
          <MapPin className="w-4 h-4 shrink-0" />
          {restaurant.address_text}
          {cityLabel ? `, ${cityLabel}` : ""}
        </p>
      )}
      {restaurant.phone && (
        <p className="text-gray-600 dark:text-gray-400">
          <span className="font-medium text-gray-800 dark:text-gray-200">Teléfono: </span>
          <a href={`tel:${restaurant.phone}`} className="text-blue-600 hover:underline">
            {restaurant.phone}
          </a>
        </p>
      )}
      {hoursSummary && (
        <p className="text-gray-600 dark:text-gray-400">
          <span className="font-medium text-gray-800 dark:text-gray-200">Horario: </span>
          {hoursSummary}
        </p>
      )}
      {restaurant.has_terraza && (
        <p className="text-gray-600 dark:text-gray-400">Terraza disponible</p>
      )}
      {restaurant.website_url && (
        <a
          href={restaurant.website_url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 text-blue-600 hover:underline"
          onClick={() => {
            void logRestaurantEngagement(supabase, {
              restaurantId,
              eventType: "website_tap",
            });
          }}
        >
          <ExternalLink className="w-4 h-4" />
          Sitio web
        </a>
      )}
      {showInstagram && igHandle && (
        <a
          href={`https://instagram.com/${igHandle}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:underline"
          onClick={() => {
            void logRestaurantEngagement(supabase, {
              restaurantId,
              eventType: "instagram_tap",
            });
          }}
        >
          @{igHandle}
        </a>
      )}
      {isClaimed === false && (
        <Link
          href={`/download?restaurant_id=${encodeURIComponent(restaurantId)}`}
          className="inline-flex mt-2 rounded-lg bg-violet-600 text-white px-4 py-2 text-sm font-semibold hover:bg-violet-700"
        >
          {rs.claimBanner?.cta ?? "Gestionar en la app"}
        </Link>
      )}
      {isClaimed && (
        <p className="text-xs text-emerald-700 dark:text-emerald-400 font-medium">
          Perfil gestionado por el restaurante
        </p>
      )}
    </div>
  );
}

function DirectionsTab({
  address,
  directionsHref,
  restaurantName,
  restaurantId,
}: {
  address?: string | null;
  directionsHref: string | null;
  restaurantName: string | null;
  restaurantId: string;
}) {
  return (
    <div className="space-y-4">
      {address && <p className="text-sm text-gray-700 dark:text-gray-300">{address}</p>}
      {directionsHref ? (
        <a
          href={directionsHref}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 text-white px-4 py-2.5 text-sm font-semibold hover:bg-blue-700"
          onClick={() => {
            void logRestaurantEngagement(supabase, {
              restaurantId,
              eventType: "directions_tap",
            });
          }}
        >
          <Navigation className="w-4 h-4" />
          Abrir en Google Maps
        </a>
      ) : (
        <p className="text-sm text-gray-500">No hay ubicación disponible.</p>
      )}
      {directionsHref && restaurantName && (
        <div className="rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 aspect-video bg-gray-100">
          <iframe
            title={`Mapa de ${restaurantName}`}
            src={`https://maps.google.com/maps?q=${encodeURIComponent(address ?? restaurantName)}&output=embed`}
            className="w-full h-full min-h-[200px] border-0"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      )}
    </div>
  );
}

function ClaimTab({
  restaurantId,
  restaurantName,
  isClaimed,
}: {
  restaurantId: string;
  restaurantName: string | null;
  isClaimed?: boolean;
}) {
  const { t } = useLanguage();
  const rs = t.restaurantSharePage;
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
        {rs.claimTabIntro ??
          "¿Eres el propietario o gestionas este restaurante? Descarga MenúHunter, crea una cuenta y gestiona la ficha desde la app."}
      </p>
      <ClaimBanner
        restaurantId={restaurantId}
        restaurantName={restaurantName ?? undefined}
        isClaimed={isClaimed}
      />
      <Link
        href={`/download?restaurant_id=${encodeURIComponent(restaurantId)}`}
        className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-2 text-sm font-semibold hover:bg-gray-50 dark:hover:bg-gray-800"
      >
        {rs.claimTabButton ?? "Gestionar en la app"}
      </Link>
    </div>
  );
}

